/**
 * components/client-detail/CaseAttachmentModal.tsx
 *
 * Modal for attaching a pending request (case_id = NULL) to a client dossier.
 * Shown automatically on ClientDetail when the client has unattached completed requests.
 * Lists all existing dossiers (name only) + a "Créer un nouveau dossier" card.
 * If multiple pending requests exist, handles them one by one (wizard pattern).
 * Created Apr 2026.
 * Updated Apr 2026 — try/finally in handleAttach; tabIndex/-1 + focus on mount for
 *   reliable Escape handling; input extracted outside <button> to fix HTML semantics.
 * Updated Apr 2026 — replaced currentIndex/pending[index] with a stable localPending
 *   queue so parent refreshes of the pending prop no longer break the wizard steps;
 *   input disabled while loading with keyboard-guard; removed unused clientId prop.
 */

import { useEffect, useRef, useState } from 'react';
import { FolderOpen, FolderPlus, ChevronRight, Loader2 } from 'lucide-react';
import { formatDate } from '../../utils/dateUtils';
import { buildCaseName } from '../../utils/caseNaming';
import type { ClientCase } from '../../hooks/useClientCases';
import type { PendingRequest } from '../../hooks/usePendingAttachments';

interface CaseAttachmentModalProps {
  pending: PendingRequest[];
  cases: ClientCase[];
  onAttach: (requestId: string, caseId: string) => Promise<boolean>;
  onCreateAndAttach: (requestId: string, name: string) => Promise<boolean>;
  onDismiss: () => void;
}

export default function CaseAttachmentModal({
  pending,
  cases,
  onAttach,
  onCreateAndAttach,
  onDismiss,
}: CaseAttachmentModalProps) {
  // Stable local queue — initialized once from pending so parent refreshes don't
  // corrupt the wizard position.
  const [localPending, setLocalPending] = useState<PendingRequest[]>(() => [...pending]);
  const totalRef = useRef(pending.length);

  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(
    cases.length > 0 ? cases[0].id : 'new'
  );
  const [creatingNew, setCreatingNew] = useState(cases.length === 0);
  const [newCaseName, setNewCaseName] = useState('');
  const [loading, setLoading] = useState(false);
  const newCaseInputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const currentRequest = localPending[0];
  const total = totalRef.current;
  const doneCount = total - localPending.length;

  // Focus the wrapper on mount so Escape key can be captured
  useEffect(() => {
    wrapperRef.current?.focus();
  }, []);

  // Reset selection/name whenever the head of the queue changes
  useEffect(() => {
    const defaultCaseId = cases.length > 0 ? cases[0].id : 'new';
    setSelectedCaseId(defaultCaseId);
    setCreatingNew(cases.length === 0);
    setNewCaseName(
      currentRequest?.completed_at
        ? buildCaseName(currentRequest.completed_at)
        : buildCaseName(new Date())
    );
  }, [localPending.length, cases, currentRequest?.completed_at]);

  useEffect(() => {
    if (creatingNew && newCaseInputRef.current) {
      newCaseInputRef.current.focus();
      newCaseInputRef.current.select();
    }
  }, [creatingNew]);

  if (!currentRequest) return null;

  const handleSelectCase = (caseId: string) => {
    setSelectedCaseId(caseId);
    setCreatingNew(false);
  };

  const handleSelectNew = () => {
    setSelectedCaseId('new');
    setCreatingNew(true);
  };

  const handleAttach = async () => {
    if (loading) return;
    setLoading(true);
    let ok = false;
    try {
      if (creatingNew) {
        const name = newCaseName.trim() || buildCaseName(currentRequest.completed_at ?? new Date());
        ok = await onCreateAndAttach(currentRequest.id, name);
      } else if (selectedCaseId) {
        ok = await onAttach(currentRequest.id, selectedCaseId);
      }
    } finally {
      setLoading(false);
    }
    if (!ok) return;
    const next = localPending.slice(1);
    if (next.length > 0) {
      setLocalPending(next);
    } else {
      onDismiss();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') onDismiss();
  };

  return (
    <div
      ref={wrapperRef}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 outline-none"
      role="dialog"
      aria-modal="true"
      aria-labelledby="case-modal-title"
      tabIndex={-1}
      onKeyDown={handleKeyDown}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

      {/* Panel */}
      <div className="relative bg-white rounded-2xl shadow-soft-xl w-full max-w-md flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-neutral-100 flex-shrink-0">
          {total > 1 && (
            <p className="text-xs font-medium text-neutral-400 mb-1.5">
              {doneCount + 1} sur {total}
            </p>
          )}
          <h2 id="case-modal-title" className="text-base font-semibold text-neutral-900 leading-snug">
            À quel dossier rattacher cette réponse ?
          </h2>
          {/* Request summary */}
          <div className="mt-3 flex items-start gap-2.5">
            <div className="mt-0.5 flex-shrink-0 w-7 h-7 rounded-lg bg-primary-50 flex items-center justify-center">
              <FolderOpen className="w-3.5 h-3.5 text-primary-600" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-neutral-800 truncate leading-snug">
                {currentRequest.title}
              </p>
              {currentRequest.completed_at && (
                <p className="text-xs text-neutral-400 mt-0.5">
                  Complété le {formatDate(currentRequest.completed_at)}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Dossier list */}
        <div className="px-6 py-4 overflow-y-auto flex-1 space-y-2">
          {cases.map((c) => {
            const isSelected = !creatingNew && selectedCaseId === c.id;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => handleSelectCase(c.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 ${
                  isSelected
                    ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500'
                    : 'border-neutral-200 bg-white hover:border-neutral-300 hover:bg-neutral-50'
                }`}
              >
                <FolderOpen
                  className={`w-4 h-4 flex-shrink-0 ${isSelected ? 'text-primary-600' : 'text-neutral-400'}`}
                />
                <span className={`text-sm flex-1 truncate ${isSelected ? 'font-medium text-primary-700' : 'text-neutral-700'}`}>
                  {c.name}
                </span>
                {isSelected && <ChevronRight className="w-4 h-4 text-primary-500 flex-shrink-0" />}
              </button>
            );
          })}

          {/* Create new dossier — standalone input when active, button when idle */}
          {creatingNew ? (
            <div className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-primary-500 bg-primary-50 ring-1 ring-primary-500">
              <FolderPlus className="w-4 h-4 flex-shrink-0 text-primary-600" />
              <input
                ref={newCaseInputRef}
                type="text"
                value={newCaseName}
                onChange={(e) => setNewCaseName(e.target.value)}
                onKeyDown={(e) => {
                  if (loading) return;
                  if (e.key === 'Enter') { e.preventDefault(); handleAttach(); }
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    if (cases.length > 0) {
                      setCreatingNew(false);
                      setSelectedCaseId(cases[0].id);
                    } else {
                      onDismiss();
                    }
                    e.stopPropagation();
                  }
                }}
                disabled={loading}
                placeholder="Nom du nouveau dossier"
                className="flex-1 text-sm bg-transparent outline-none text-primary-700 font-medium placeholder:text-primary-400 placeholder:font-normal min-w-0 disabled:opacity-60 disabled:cursor-not-allowed"
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={handleSelectNew}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-dashed border-neutral-300 bg-white hover:border-primary-400 hover:bg-primary-50/40 text-left transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600"
            >
              <FolderPlus className="w-4 h-4 flex-shrink-0 text-neutral-400" />
              <span className="text-sm text-neutral-500 flex-1">Créer un nouveau dossier</span>
            </button>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-neutral-100 flex-shrink-0 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={onDismiss}
            className="text-sm text-neutral-500 hover:text-neutral-700 transition-colors px-2 py-1.5 rounded-lg hover:bg-neutral-100"
          >
            Plus tard
          </button>
          <button
            type="button"
            onClick={handleAttach}
            disabled={loading || (!creatingNew && !selectedCaseId)}
            className="flex items-center gap-2 px-5 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : null}
            {total > 1 && localPending.length > 1 ? 'Suivant' : 'Rattacher'}
          </button>
        </div>
      </div>
    </div>
  );
}
